import type { CreateFormData } from "@/components/create/types";
import type { AgentPhase } from "@/types/createAgent";
import type { DesignSpec } from "@/types/designSpec";
import {
  evaluateAgentActionReadiness,
  hasConfirmedBackDesignDirection,
} from "@/lib/agent/actionReadinessGuards";

export interface DesignerWorkspaceActionsInput {
  phase: AgentPhase;
  formState: Partial<CreateFormData>;
  /** Contract being quoted; this can be the saved preview while edits await Apply. */
  designSpec?: DesignSpec | null;
  hasFrontImage: boolean;
  hasBackImage: boolean;
  storedFrontImagePath?: string | null;
  workspaceBusy?: boolean;
  detailsEditing?: boolean;
  quoteSubmitting?: boolean;
  hasPendingRenderedChanges?: boolean;
  /** Platform auth/ticket/upload gates; no authority is inferred from chat copy. */
  tryOnAvailable: boolean;
  tryOnDisabled?: boolean;
  tryOnDisabledReason?: string;
}

/** Shared availability of the stable workspace's secondary actions. */
export function projectDesignerWorkspaceActions(
  input: DesignerWorkspaceActionsInput
) {
  const quoteReadiness = evaluateAgentActionReadiness({
    actionId: "submit_quote",
    phase: input.phase,
    formState: input.formState,
    hasFrontImage: input.hasFrontImage,
    hasBackImage: input.hasBackImage,
    frontApproved: input.formState.frontApproved === true,
    hasBackDesignDirection: hasConfirmedBackDesignDirection(input.designSpec),
  });
  const quoteReason = input.quoteSubmitting
    ? "Your quote is being prepared."
    : input.detailsEditing
      ? "Save or cancel your edits first."
      : input.workspaceBusy
        ? "Wait for the current update to finish."
        : !input.storedFrontImagePath?.trim()
          ? "Save a front preview before requesting a quote."
          : !quoteReadiness.allowed
            ? quoteReadiness.message
            : null;
  const tryOnReason = input.detailsEditing
    ? "Save or cancel your edits first."
    : input.workspaceBusy
      ? "Wait for the current update to finish."
      : input.tryOnDisabled || !input.tryOnAvailable || !input.hasFrontImage
        ? (input.tryOnDisabledReason ??
          (!input.hasFrontImage
            ? "Generate the front preview to unlock free try-on."
            : "Free try-on is temporarily unavailable."))
        : null;
  return {
    quote: {
      enabled: !quoteReason,
      unavailableReason: quoteReason,
      requiresPendingConfirmation: Boolean(input.hasPendingRenderedChanges),
    },
    tryOn: {
      enabled: !tryOnReason,
      unavailableReason: tryOnReason,
      requiresPendingConfirmation: Boolean(input.hasPendingRenderedChanges),
    },
  };
}

/** Permanent secondary actions have one owner in the shared design workspace. */
export function isWorkspaceSecondaryAction(actionId: string): boolean {
  return actionId === "submit_quote" || actionId === "start_virtual_try_on";
}
